import {useCallback, useEffect, useLayoutEffect, useState} from 'react'
import {useApolloClient} from '@apollo/client'
import {useParams} from 'react-router-dom'

import {
  fromPersistedCookieOptions,
  getOptions,
  saveOptions,
  setAnalytics,
  toPersistedCookieOptions,
} from '@sdox/utils/dom'
import {IsClpAvailableDocument, IsFolderItemAvailableDocument} from '@sdox/api'

import type {CookieOption, PersistedCookieOptions} from '@sdox/utils/dom'
import type {
  ContactPersonFragment,
  IsClpAvailableQuery,
  IsClpAvailableQueryVariables,
  IsFolderItemAvailableQuery,
  IsFolderItemAvailableQueryVariables,
} from '@sdox/api'

export interface CookieProviderProps {
  cookiePolicy?: string
  cookieOptions?: CookieOption[]
  contactPerson?: ContactPersonFragment
  imprintUrl?: string
}

export enum CookieKeysEnum {
  Mandatory = '0',
  Performance = '3',
  Analysis = '4',
}

export const useCookies = ({cookieOptions = []}: CookieProviderProps) => {
  const client = useApolloClient()
  const {seFolderSlug, documentId, clpSlug} = useParams()
  const [selectedCookieOptions, setSelectedCookieOptions] = useState<CookieOption[]>([])
  // set to truthy value to prevent FOUC
  const [persistedOptions, setPersistedOptions] = useState<PersistedCookieOptions | null>({})

  const acceptCookies = useCallback((selectedOptions: CookieOption[]) => {
    const persistableOptions = toPersistedCookieOptions(
      cookieOptions,
      selectedOptions,
    )

    saveOptions(persistableOptions)

    const trackingOption = selectedOptions.find((o) => o.key === Number(CookieKeysEnum.Performance))
    setAnalytics(!!trackingOption)

    const analysisOption = selectedOptions.find((o) => o.key === Number(CookieKeysEnum.Analysis))

    //  fire a request to gql server for SE folder (analytics)
    if (analysisOption && seFolderSlug && documentId){
      client.query<IsFolderItemAvailableQuery, IsFolderItemAvailableQueryVariables>({
        query: IsFolderItemAvailableDocument, variables:{
          exposeFolderRecordId: documentId,
          slug: seFolderSlug,
        },
        fetchPolicy: 'network-only',
      })
    }

    //  fire a request to gql server for CLP documents (analytics)
    if (analysisOption && clpSlug) {
      client.query<IsClpAvailableQuery, IsClpAvailableQueryVariables>({
        query: IsClpAvailableDocument, variables:{
          clpSlug: clpSlug,
        },
        fetchPolicy: 'network-only',
      })
    }

    setSelectedCookieOptions(selectedOptions)
  }, [cookieOptions, seFolderSlug, documentId, clpSlug, client])

  const acceptAllCookies = useCallback(() => {
    acceptCookies(cookieOptions)
  }, [acceptCookies, cookieOptions])

  const revokeAllCookies = useCallback(() => {
    setSelectedCookieOptions([])
    saveOptions(toPersistedCookieOptions(cookieOptions, []))
  }, [cookieOptions])

  useEffect(() => {
    if (persistedOptions) {
      setSelectedCookieOptions(
        fromPersistedCookieOptions(cookieOptions, persistedOptions),
      )
    } else {
      const preselected = cookieOptions.filter((o) => o.vital || !!o.default)
      setSelectedCookieOptions(preselected)
    }
  }, [cookieOptions, persistedOptions])

  useEffect(() => {
    setPersistedOptions(getOptions())
  }, [])

  return {
    revokeAllCookies,
    acceptAllCookies,
    acceptCookies,
    cookieOptions,
    selectedCookieOptions,
    persistedOptions,
  }
}
